// ═══════════════════════════════════════════════════════════════════════════
// ordenParaCascada (FRONT) — Normalizador: orden CANÓNICA → shape que consume
// la cascada de rentabilidad del dashboard (espejo de functions/lib/ordenParaCascada.js)
// ═══════════════════════════════════════════════════════════════════════════
//
// MISMA regla que el backend (Bloque 5/7): la cascada y sus helpers
// (`ordenComisionML`, `envioSellerDeOrden` (repartoEnvio.js), `calcularRentabilidadLinea`, los
// clasificadores) consumen el shape legacy de mlOrdenes. La canónica `ordenes`
// guarda lo mismo con otros nombres:
//
//   order_items   ≈ datosCanal.orderItemsRaw   (raw ML, incluye sale_fee)
//   payments      ≈ datosCanal.paymentsRaw     (raw ML, marketplace_fee/status)
//   total_amount  ≈ montoTotal                 (núcleo)
//   date_created  ≈ fechaCreada                (núcleo, ISO string)
//   _enriched     ≈ datosCanal.enriquecimiento (MISMO shape interno — el
//                   enricher canónico reusa los mappers del legacy)
//   _rentabilidad ≈ rentabilidad               (derivado, escrito por Capa 1)
//
// Adaptador TRANSICIONAL declarado (igual que el del backend): mantiene los
// helpers probados intactos y garantiza paridad Δ0 FE↔BE. El refactor a
// camelCase nativo va con el retiro del legacy (Bloque 9), no acá.
//
// Función PURA — no toca Firestore.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convierte un doc de `ordenes` (como lo devuelve useOrdenesPeriodo, con
 * `_fechaCreada` Date ya parseada) al shape legacy de la cascada.
 *
 * `id` = externalId (el id ML) — los dashboards agrupan/linkean por ese id,
 * NO por el docId autogenerado de Firestore (que va en `_docId`).
 */
export function ordenCanonicaAShapeCascada(doc) {
  const dc = doc?.datosCanal || {}
  return {
    id: doc?.externalId ?? null,
    _docId: doc?.id ?? null,
    empresaId: doc?.empresaId ?? null,
    canal: doc?.canal ?? null,
    conexionId: doc?.conexionId ?? null,
    // Estado CANÓNICO (pagada/confirmada/pendiente/cancelada) — los predicados
    // esFacturable/esCancelado de useOrdenesPeriodo leen este campo.
    estado: doc?.estado ?? null,
    order_items: Array.isArray(dc.orderItemsRaw) ? dc.orderItemsRaw : [],
    payments: Array.isArray(dc.paymentsRaw) ? dc.paymentsRaw : [],
    // Solo FRONT (el backend no lo necesita): los dashboards Flex/Distribución
    // tienen cadenas de fallback `o.shipping?.receiver_address || _enriched...`.
    // shippingRaw es el shipping crudo de /orders/search (a veces trae address).
    shipping: dc.shippingRaw || null,
    // Solo FRONT: CSV exports leen buyer.nickname/id; FlexPerformance lee
    // tags raíz (cancelBy). Ambos viven en datosCanal.
    buyer: dc.buyer || null,
    tags: Array.isArray(dc.tags) ? dc.tags : [],
    total_amount: doc?.montoTotal ?? null,
    // Sincro A, A.6b-4 parte 3: la señal «sin envío» del núcleo, que lee
    // `envioSellerDeOrden`. Sólo si la clave vino: un doc sin ella deja el
    // envío en «no informado» (falla cerrado), nunca en un 0 conocido.
    ...(doc && Object.prototype.hasOwnProperty.call(doc, 'envioId') ? { envioId: doc.envioId } : {}),
    date_created: doc?.fechaCreada ?? null,
    _dateCreated: doc?._fechaCreada ?? (doc?.fechaCreada ? new Date(doc.fechaCreada) : null),
    _enriched: dc.enriquecimiento || null,
    _rentabilidad: doc?.rentabilidad || undefined,
  }
}
